import { CopyEmail } from "@/components";
import { motion } from "framer-motion";
import { Mail, MapPin, Phone } from "lucide-react";
import { useState } from "react";

export const ContactPage = () => {
  return (
    <div className="bg-white">
      <HeaderSection />
      <OfficeGrid />
      <InquirySection />
      <MapSection />
    </div>
  );
};

// 1. The Header (Minimalist)
const HeaderSection = () => {
  return (
    <section className="pt-40 pb-20 px-6 md:px-12 lg:px-24 bg-white text-center">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="max-w-4xl mx-auto flex flex-col items-center">
        <h1 className="font-serif text-5xl md:text-7xl text-black mb-6">
          Start Your Project.
        </h1>
        <p className="text-neutral-500 text-xl font-light mb-8 max-w-2xl">
          Reach out to our engineering teams in Delhi or Mumbai for civil,
          mechanical, or luxury interior inquiries.
        </p>
        <motion.div
          initial={{ height: 0 }}
          animate={{ height: "6rem" }}
          transition={{ duration: 0.8, delay: 0.4 }}
          className="w-px bg-primary"
        />
      </motion.div>
    </section>
  );
};

// 2. The Office Grid (The Physical Presence)
const OfficeGrid = () => {
  return (
    <section className="py-20 px-6 md:px-12 lg:px-24 bg-white">
      <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-16">
        <OfficeCard
          label="Headquarters"
          city="Delhi"
          address="Gali No. 2, Dabri Industrial Area, New Delhi – 110045."
          phones={["+91-9868155464", "9868155465"]}
          emails={["mail@arihantinfracon.com"]}
        />
        <OfficeCard
          label="Mumbai Hub"
          city="Mumbai"
          address="Sh. No. 2, Ground Floor, Sai Pooja Apartments, J B Nagar, Andheri East, Mumbai - 400059."
          phones={["+91-9650805062", "9833781773"]}
          emails={["arihantinfracon.bom@gmail.com"]}
        />
      </div>
    </section>
  );
};

const OfficeCard = ({
  label,
  city,
  address,
  phones,
  emails,
}: {
  label: string;
  city: string;
  address: string;
  phones: string[];
  emails: string[];
}) => {
  return (
    <motion.div
      initial={{ opacity: 0, y: 30 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="group bg-neutral-50 p-10 md:p-14 border border-transparent hover:border-neutral-900 transition-colors duration-500 min-h-100 flex flex-col justify-between">
      <div>
        <div className="flex items-center gap-3 mb-8">
          <div className="w-2 h-2 bg-primary rounded-full" />
          <span className="text-primary text-sm uppercase tracking-widest font-semibold">
            {label}
          </span>
        </div>
        <h3 className="font-serif text-3xl md:text-4xl text-neutral-900 mb-8">
          {city}
        </h3>
        <p className="text-neutral-600 font-light leading-relaxed text-lg mb-8 max-w-sm">
          {address}
        </p>
      </div>

      <div className="space-y-4">
        <div className="flex items-start gap-4">
          <Phone className="w-5 h-5 text-neutral-400 mt-1" />
          <div className="flex flex-col gap-1">
            {phones.map((phone, i) => (
              <a
                key={i}
                href={`tel:${phone.replace(/[^0-9+]/g, "")}`}
                className="text-neutral-800 hover:text-primary transition-colors">
                {phone}
              </a>
            ))}
          </div>
        </div>
        <div className="flex items-start gap-4">
          <Mail className="w-5 h-5 text-neutral-400 mt-1" />
          <div className="flex flex-col gap-1">
            {emails.map((email, i) => (
              <CopyEmail
                key={i}
                email={email}
                className="text-neutral-800 hover:text-primary transition-colors"
              />
            ))}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// 3. The Inquiry Interface (The Form)
const InquirySection = () => {
  const [projectType, setProjectType] = useState("Civil Infrastructure");

  const types = [
    "Civil Infrastructure",
    "Luxury Retail",
    "Airport Maintenance",
    "Other",
  ];

  return (
    <section className="py-24 px-6 md:px-12 lg:px-24 bg-neutral-900 text-white">
      <div className="max-w-2xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          className="text-center mb-16">
          <span className="text-neutral-400 uppercase tracking-widest text-sm font-medium">
            Project Consultation
          </span>
          <h2 className="font-serif text-4xl m-4">Tell us about your vision</h2>
        </motion.div>

        <form className="space-y-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
            <FormInput label="Name" placeholder="Your Name" />
            <FormInput label="Company / Brand" placeholder="Company Name" />
          </div>
          <FormInput label="Email" placeholder="your@email.com" type="email" />

          <div className="space-y-4">
            <label className="text-sm uppercase tracking-widest text-neutral-500 font-medium block">
              Project Type
            </label>
            <div className="flex flex-wrap gap-4">
              {types.map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setProjectType(type)}
                  className={`px-6 py-3 border text-sm transition-all duration-300 ${
                    projectType === type
                      ? "bg-white text-neutral-900 border-white"
                      : "bg-transparent text-neutral-400 border-neutral-700 hover:border-neutral-500"
                  }`}>
                  {type}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            <label className="text-sm uppercase tracking-widest text-neutral-500 font-medium block">
              Message / Project Scope
            </label>
            <textarea
              className="w-full bg-transparent border-b border-neutral-700 text-white pb-4 focus:outline-none focus:border-white transition-colors text-lg min-h-25 resize-none"
              placeholder="Brief details about your project requirements..."
            />
          </div>

          <div className="pt-8 text-center">
            <motion.button
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="px-12 py-4 bg-white text-neutral-900 font-medium uppercase tracking-widest hover:bg-primary hover:text-white transition-colors duration-300 w-full md:w-auto min-w-50">
              Submit Inquiry
            </motion.button>
          </div>
        </form>
      </div>
    </section>
  );
};

const FormInput = ({
  label,
  placeholder,
  type = "text",
}: {
  label: string;
  placeholder: string;
  type?: string;
}) => (
  <div className="space-y-2">
    <label className="text-sm uppercase tracking-widest text-neutral-500 font-medium block">
      {label}
    </label>
    <input
      type={type}
      className="w-full bg-transparent border-b border-neutral-700 text-white pb-3 focus:outline-none focus:border-white transition-colors text-lg"
      placeholder={placeholder}
    />
  </div>
);

// 4. The Map Visual (Regional Presence)
const MapSection = () => {
  return (
    <section className="py-24 px-6 bg-neutral-100 flex flex-col items-center justify-center overflow-hidden">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        className="relative max-w-4xl w-full aspect-video bg-neutral-900 rounded-lg overflow-hidden shadow-2xl flex items-center justify-center">
        {/* Placeholder for map graphic */}
        <div className="absolute inset-0 z-0 opacity-60">
          {/* Satellite View of India at Night */}
          <img
            src="/images/india-night-map.jpg"
            alt="Satellite Map of India"
            className="w-full h-full object-cover brightness-110 contrast-125 saturate-0 md:saturate-100" // Enhanced brightness and contrast, colors visible on desktop
            style={{ objectPosition: "center 30%" }}
          />
        </div>
        <div className="absolute inset-0 bg-neutral-900/40 mix-blend-multiply" />

        {/* Glowing Dots (Representing Hubs in India Configuration) */}
        {/* Delhi (North, Central) */}
        <MapMarker top="26%" left="42%" label="Delhi" />
        {/* Mumbai (South-West) */}
        <MapMarker top="78%" left="30%" label="Mumbai" />
        {/* Lucknow (South-East of Delhi) */}
        <MapMarker top="44%" left="52%" label="Lucknow" />
        {/* Jaipur (South-West of Delhi) */}
        <MapMarker top="42%" left="38%" label="Jaipur" />

        <div className="absolute bottom-8 right-8 bg-white/10 backdrop-blur-md p-4 border border-white/20 z-10 hidden md:block">
          <div className="flex items-center gap-3">
            <MapPin className="text-primary w-5 h-5" />
            <span className="text-white text-sm font-mono tracking-widest uppercase">
              Active Operational Hubs
            </span>
          </div>
        </div>
      </motion.div>
      <p className="mt-8 text-neutral-500 text-sm uppercase tracking-widest text-center">
        Active Operational Hubs across Major Airports
      </p>
    </section>
  );
};

const MapMarker = ({
  top,
  left,
  label,
}: {
  top: string;
  left: string;
  label: string;
}) => (
  <motion.div
    initial={{ opacity: 0 }}
    whileInView={{ opacity: 1 }}
    transition={{ delay: 0.5 }}
    className="absolute w-20 flex flex-col items-center gap-2 group cursor-default z-10"
    style={{ top, left }}>
    <div className="relative">
      <div className="w-3 h-3 bg-primary rounded-full relative z-10" />
      <div className="absolute inset-0 bg-primary rounded-full animate-ping opacity-75" />
    </div>
    <span className="text-xs text-white uppercase tracking-widest font-semibold opacity-0 group-hover:opacity-100 transition-opacity bg-black/50 px-2 py-1 rounded backdrop-blur-sm">
      {label}
    </span>
  </motion.div>
);
